import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import CategoryManager from "../components/CategoryManager";

const API_URL = process.env.REACT_APP_API_BASE_URL

const Dashboard = () => {
  const [galleries, setGalleries] = useState([]);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserGalleries = async () => {
      try {
        const res = await axios.get(API_URL + "/api/galleries/user", {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Use the cover_image field from the DB for thumbnails, sort by created_at descending
        const galleriesWithThumbnails = res.data.galleries
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map((gallery) => {
            const coverImage = gallery.cover_image
              ? `https://imagedelivery.net/Z1o5jP3Z0wrLOM-5XDvR8A/${gallery.cover_image}/thumbnail`
              : null;
            return { ...gallery, coverImage, hasPassword: Boolean(gallery.has_password) };
          });

        setGalleries(galleriesWithThumbnails);
      } catch (err) {
        console.error("❌ Error fetching galleries:", err.response?.data || err.message);
        setError("Failed to load your galleries.");
      }
    };

    fetchUserGalleries();
  }, [token]);

  const handleDeleteGallery = async (galleryId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this gallery? This action cannot be undone.");
    if (!confirmDelete) return;
    setIsDeleting(true);
    try {
      const res = await axios.delete(API_URL + `/api/galleries/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 200) {
        setGalleries((prevGalleries) => prevGalleries.filter((gallery) => gallery.id !== galleryId));
      }
    } catch (err) {
      console.error("❌ Error deleting gallery:", err.response?.data || err.message);
      alert("Failed to delete the gallery.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className="p-6 max-w-6xl mx-auto my-32">
      {isDeleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-gray-800 bg-opacity-75 text-white px-6 py-4 rounded-md">
            Deleting gallery...
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Your Collections</h1>
      </div>

      {/* Actions */}
      <div className="flex justify-end items-center mb-6 space-x-4">
        <Link
          to="/create-gallery"
          className="bg-blue-500 text-white px-5 py-2 rounded-md hover:bg-blue-600 transition"
        >
          + Create Collection
        </Link>
        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition"
        >
          Logout
        </button>
      </div>

      {/* Error Message */}
      {error && <p className="text-red-500 text-center">{error}</p>}

      <CategoryManager />

      {/* No Galleries Message */}
      {galleries.length === 0 ? (
        <p className="mt-6 text-center text-gray-500 text-lg">No galleries found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {galleries.map((gallery) => {
            const detailPath = gallery.slug ? `/galleries/${gallery.slug}` : `/gallery/${gallery.id}`;
            return (
              <div key={gallery.id} className="relative group overflow-hidden shadow-lg hover:shadow-xl transition">
              <Link to={detailPath} className="block relative w-full" style={{ aspectRatio: "3 / 2" }}>
                {/* Background Image */}
                {gallery.coverImage ? (
                  <img
                    src={gallery.coverImage}
                    alt={gallery.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 transform scale-100 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center text-gray-600">
                    No Cover Image
                  </div>
                )}
                {gallery.hasPassword && (
                  <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-2 py-1 rounded shadow flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm3 8H9V7a3 3 0 016 0v3z" />
                    </svg>
                    <span>Password</span>
                  </div>
                )}
                <span className="absolute bottom-3 left-3 rounded-full bg-black/65 px-2.5 py-1 text-xs text-white">
                  {gallery.category_name || "Uncategorized"}
                </span>
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                {/* Gallery Title - Centered */}
                <h2 className="absolute inset-0 flex items-center drop-shadow-lg justify-center text-white text-lg font-semibold text-center px-2">
                  {gallery.title}
                </h2>
              </Link>
              {/* Delete Button */}
              <button
                onClick={() => handleDeleteGallery(gallery.id)}
                className="absolute top-3 right-3 bg-red-500 text-white text-sm px-2 py-1 rounded opacity-75 hover:opacity-100 transition"
                title="Delete Gallery"
              >
                ✕
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
